/**
 * Prop Studio — the candidate panel: what the selected prop has now (source,
 * licence, files and where they are on disk, a stale or edited warning with
 * Reconvert), and the three ways to give it a model — a BlenderKit search, a
 * pasted asset id or page address, or a file of your own — under one Textures
 * choice that governs both BlenderKit routes.
 *
 * The search is the panel's own (it is only a read); assigning, uploading,
 * reconverting and removing change the library, so the page does those and
 * tells the panel when it is busy. Giving a prop that already has a model
 * another one deletes the current files, so every such route confirms first —
 * in stronger words when those files are the only copy (a file of your own,
 * or a model edited in Blender since it came down).
 */

import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { designerControlStyle as control } from '../designer/DesignerHost';
import type { PropDef } from '../../calculations/staging/kits';
import { LibraryEntry, MODEL_FORMATS, MODEL_UNITS, ModelUnits, SearchResult, ServiceDownError, isModelFile, modelPath, searchModels } from './studioApi';
import { extractAssetId } from './assetId';
import { PROP_STUDIO_STRINGS as S } from './strings';
import { isPartKey } from './variants';

export type LibraryBusy = 'assign' | 'upload' | 'reconvert' | 'remove' | null;

export interface CandidatePanelProps {
  def: PropDef;
  /** The library key in hand: the prop's own id, or `<id>~n` for a further model of it (variants). */
  entryKey: string;
  entry: LibraryEntry | undefined;
  /** False until GET /api/library has answered — nothing can be assigned before that. */
  libraryLoaded: boolean;
  /** The helper's library folder on disk, when it said (older helpers do not). */
  libraryDir?: string;
  busy: LibraryBusy;
  onAssign: (asset: string, resolution: string) => void;
  onUpload: (file: File, units: ModelUnits) => void;
  onReconvert: () => void;
  onRemove: () => void;
  /** An error from the panel's own search, for the page's notice line. */
  onError: (err: unknown) => void;
  /** Rendered between the current model and the ways to pick one: the placement controls. */
  children?: React.ReactNode;
}

const ACCENT = '#FEB100';
const MUTED = '#9a9ca6';
const OK = '#9fe0b0';
const WARN = '#ffd98a';
const MONO = 'ui-monospace, monospace';
const LABEL: React.CSSProperties = { fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 };
const CARD: React.CSSProperties = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: 10 };
const BUTTON: React.CSSProperties = { ...control, cursor: 'pointer' };
const PRIMARY: React.CSSProperties = { ...control, background: ACCENT, color: '#000', border: 'none', fontWeight: 700, cursor: 'pointer' };
const WARNING_BOX: React.CSSProperties = { marginTop: 4, padding: '6px 8px', borderRadius: 6, background: 'rgba(254,177,0,0.12)', border: `1px solid ${ACCENT}`, color: WARN };
const RESOLUTIONS = ['resolution_0_5K', 'resolution_1K', 'resolution_2K', 'resolution_4K', 'blend'];
const ORIGINAL = 'blend';

const mb = (bytes: number | undefined): string => (typeof bytes === 'number' ? (bytes / 1048576).toFixed(1) : '?');
const metres = (n: number | undefined): string => (typeof n === 'number' ? n.toFixed(2) : '?');
const licence = (code: string | undefined): string => (code ? S.licenceName[code] ?? code : '–');
const when = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export const CandidatePanel: React.FC<CandidatePanelProps> = ({ def, entryKey, entry, libraryLoaded, libraryDir, busy, onAssign, onUpload, onReconvert, onRemove, onError, children }) => {
  const [query, setQuery] = useState('');
  const [freeOnly, setFreeOnly] = useState(true);
  const [resolution, setResolution] = useState('resolution_1K');
  const [units, setUnits] = useState<ModelUnits>('auto');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  // The results open in a window over the page, not down the side bar; a
  // chosen model, Close or Escape puts them away.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const [assetId, setAssetId] = useState('');
  const [copied, setCopied] = useState(false);
  const pathBox = useRef<HTMLInputElement>(null);

  const anyBusy = busy !== null;
  const source = entry?.source;
  const files = entry?.files;
  const hasModel = !!entry?.status?.hasModel;
  const stale = hasModel && (!!entry?.status?.stale || !entry?.status?.hasGlb);
  const edited = hasModel && source?.edited === true;
  const modelIsBlend = !!files?.model && /\.blend$/i.test(files.model);
  const currentName = source?.name || files?.model || def.label;
  const path = hasModel && files?.model ? modelPath(libraryDir, entryKey, files.model) : null;
  const extracted = extractAssetId(assetId);

  // Results found for one prop must not be a click away from replacing
  // another: they go when the prop changes (the query stays, to search again).
  useEffect(() => {
    setResults([]);
    setSearched(false);
    setHasMore(false);
    setPage(1);
  }, [entryKey]);

  /**
   * Before a model replaces the one the prop has. The current files are
   * deleted; a BlenderKit asset can be fetched again by its id, but a file of
   * the user's own, or one edited in Blender since it came down, has no other
   * copy — those get the stronger words.
   */
  const confirmReplace = (next: string): boolean => {
    if (!hasModel) return true;
    const why = source?.kind === 'local' ? S.candidate.replaceWhyLocal
      : edited ? S.candidate.replaceWhyEdited
        : stale ? S.candidate.replaceWhyStale
          : null;
    return window.confirm(why ? S.candidate.replaceConfirmStrong(currentName, next, why) : S.candidate.replaceConfirm(currentName, next));
  };

  const runSearch = async (nextPage: number) => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const res = await searchModels(q, freeOnly, nextPage);
      const list = Array.isArray(res?.results) ? res.results : [];
      if (nextPage === 1) setResults(list);
      else setResults((prev) => [...prev, ...list]);
      setPage(nextPage);
      setHasMore(!!res?.next && list.length > 0);
      setSearched(true);
      setOpen(true);
    } catch (err) {
      onError(err);
      if (err instanceof ServiceDownError) { setResults([]); setSearched(false); }
    } finally {
      setSearching(false);
    }
  };

  const chooseFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // the same file again should count as a new upload
    if (!file) return;
    if (!isModelFile(file.name)) { onError(new Error(S.upload.badType)); return; }
    if (!confirmReplace(file.name)) return;
    onUpload(file, units);
  };

  const copyPath = async () => {
    if (!path) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(path);
      else {
        // No clipboard API (an http page that is not localhost): the old way.
        pathBox.current?.select();
        document.execCommand('copy');
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      pathBox.current?.select(); // at least leave it selected to copy by hand
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <section style={CARD}>
        <div style={LABEL}>{S.candidate.current}</div>
        {!hasModel && <p style={{ margin: '6px 0 0', fontSize: 12, color: MUTED }}>{isPartKey(entryKey) ? S.candidate.nonePart : S.candidate.none}</p>}
        {hasModel && (
          <div style={{ marginTop: 6, fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{currentName}</div>
            <div style={{ color: MUTED }}>
              {S.candidate.source[source?.kind ?? ''] ?? source?.kind}
              {source?.author ? ` · ${S.candidate.author} ${source.author}` : ''}
              {' · '}{S.candidate.licence}: {licence(source?.license)}
              {source?.resolution ? ` · ${S.search.resolutionNames[source.resolution] ?? source.resolution}` : ''}
            </div>
            {source?.note && <div style={{ color: MUTED }}>{source.note}</div>}
            {source?.page && (
              <a href={source.page} target="_blank" rel="noreferrer" style={{ color: '#9fd1ff', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {S.candidate.page} <ExternalLink size={12} />
              </a>
            )}
            <div style={{ color: MUTED, fontFamily: MONO, fontSize: 11 }}>
              {S.candidate.files}: {files?.model ?? '–'} ({S.candidate.renderer}) · {files?.glb ?? '–'} ({S.candidate.preview})
            </div>
            {path && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: MUTED, whiteSpace: 'nowrap' }}>{S.candidate.path}</span>
                  <input
                    ref={pathBox} readOnly value={path} aria-label={S.candidate.path} title={path}
                    onFocus={(e) => e.currentTarget.select()}
                    style={{ ...control, flex: 1, minWidth: 0, fontFamily: MONO, fontSize: 11, padding: '2px 6px' }}
                  />
                  <button type="button" style={{ ...BUTTON, fontSize: 11, padding: '2px 7px', whiteSpace: 'nowrap' }} onClick={copyPath}>
                    {copied ? S.candidate.copied : S.candidate.copyPath}
                  </button>
                </div>
                {modelIsBlend && <div style={{ color: MUTED }}>{S.candidate.openHint}</div>}
              </div>
            )}
            {entry?.savedAt && <div style={{ color: MUTED, fontSize: 11 }}>{S.candidate.saved(when(entry.savedAt))}</div>}
            {edited && <div style={WARNING_BOX}>{S.candidate.edited}</div>}
            {stale && <div style={WARNING_BOX}>{S.candidate.stale}</div>}
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              {modelIsBlend && (
                <button type="button" style={{ ...(stale ? PRIMARY : BUTTON), opacity: anyBusy ? 0.5 : 1 }} disabled={anyBusy} onClick={onReconvert}>
                  {busy === 'reconvert' ? S.candidate.reconverting : S.candidate.reconvert}
                </button>
              )}
              <button
                type="button" style={{ ...BUTTON, marginLeft: 'auto', opacity: anyBusy ? 0.5 : 1 }} disabled={anyBusy}
                onClick={() => { if (window.confirm(S.candidate.removeConfirm(def.label))) onRemove(); }}
              >
                {S.candidate.remove}
              </button>
            </div>
          </div>
        )}
      </section>

      {children && <section style={CARD}>{children}</section>}

      <section style={CARD}>
        <div style={LABEL}>{S.search.resolution}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 12 }}>
          <select value={resolution} aria-label={S.search.resolution} onChange={(e) => setResolution(e.target.value)} style={control}>
            {RESOLUTIONS.map((r) => <option key={r} value={r} style={{ color: '#000' }}>{S.search.resolutionNames[r] ?? r}</option>)}
          </select>
          <span style={{ color: MUTED }}>{S.search.resolutionHint}</span>
        </div>
        {resolution === ORIGINAL && <div style={WARNING_BOX}>{S.search.originalWarning}</div>}
      </section>

      <section style={CARD}>
        <div style={LABEL}>{S.search.title}</div>
        <form style={{ display: 'flex', gap: 6, marginTop: 6 }} onSubmit={(e) => { e.preventDefault(); runSearch(1); }}>
          <input
            type="search" value={query} placeholder={S.search.placeholder} aria-label={S.search.title}
            onChange={(e) => setQuery(e.target.value)} style={{ ...control, flex: 1 }}
          />
          <button type="submit" style={BUTTON} disabled={searching || !query.trim()}>{searching ? S.search.searching : S.search.go}</button>
        </form>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, fontSize: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', margin: 0 }}>
            <input type="checkbox" checked={freeOnly} onChange={(e) => setFreeOnly(e.target.checked)} style={{ accentColor: ACCENT }} /> {S.search.freeOnly}
          </label>
          {results.length > 0 && !open && (
            <button type="button" className="prop-studio-results-for" onClick={() => setOpen(true)} style={{ marginLeft: 'auto', fontSize: 11, padding: '1px 8px', borderRadius: 8, border: `1px solid ${ACCENT}`, background: 'transparent', color: ACCENT, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              {S.search.resultsFor(def.label)} · {results.length}
            </button>
          )}
        </div>
        {busy === 'assign' && <p style={{ margin: '8px 0 0', fontSize: 12, color: ACCENT }}>{S.search.busy}</p>}
        {searched && results.length === 0 && !searching && <p style={{ margin: '8px 0 0', fontSize: 12, color: MUTED }}>{S.search.none}</p>}
        {open && results.length > 0 && (
          <div
            role="dialog" aria-modal="true" aria-label={S.search.resultsFor(def.label)}
            style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
            onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          >
            <div style={{ background: '#1e2026', border: `1px solid ${ACCENT}`, borderRadius: 10, width: 'min(1100px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <span style={{ fontWeight: 700 }}>{S.search.resultsFor(def.label)}</span>
                <span style={{ fontSize: 12, color: MUTED }}>“{query.trim()}” · {results.length}</span>
                <button type="button" onClick={() => setOpen(false)} style={{ ...BUTTON, marginLeft: 'auto' }} aria-label={S.search.close}>{S.search.close}</button>
              </div>
              <div style={{ overflow: 'auto', padding: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, alignContent: 'start' }}>
            {results.map((r) => {
              const blocked = r.canDownload === false;
              const current = source?.kind === 'blenderkit' && source.asset === r.asset;
              return (
                <div key={r.asset} style={{ ...CARD, padding: 6, display: 'flex', flexDirection: 'column', gap: 4, borderColor: current ? ACCENT : undefined }}>
                  <div style={{ aspectRatio: '4 / 3', background: '#111', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {r.thumbnail ? <img src={r.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: MUTED, fontSize: 11 }}>–</span>}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.25 }} title={r.name}>{r.name}</div>
                  <div style={{ fontSize: 11, color: MUTED }}>
                    {licence(r.license)}{r.isFree === false ? ' · paid' : ''}
                    {' · '}
                    {/* The size BlenderKit gives is the original file's: what "Original file" would fetch. */}
                    {resolution === ORIGINAL ? <span style={{ color: WARN }}>{S.search.originalSize(mb(r.filesSize))}</span> : S.search.size(mb(r.filesSize))}
                  </div>
                  {r.dimensions && <div style={{ fontSize: 11, color: MUTED }}>{S.search.dims(metres(r.dimensions[0]), metres(r.dimensions[1]), metres(r.dimensions[2]))}</div>}
                  {r.author && <div style={{ fontSize: 11, color: MUTED }}>{S.candidate.author} {r.author}</div>}
                  {blocked && <div style={{ fontSize: 11, color: WARN }}>{r.canDownloadError || (r.isFree === false ? S.search.paid : S.search.cannotDownload)}</div>}
                  <button
                    type="button" style={{ ...PRIMARY, marginTop: 'auto', fontSize: 11, padding: '4px 6px', opacity: anyBusy || blocked || !libraryLoaded ? 0.5 : 1 }}
                    disabled={anyBusy || blocked || !libraryLoaded}
                    onClick={() => { if (confirmReplace(r.name)) { onAssign(r.asset, resolution); setOpen(false); } }}
                  >
                    {S.search.use}
                  </button>
                </div>
              );
            })}
              </div>
              {hasMore && (
                <div style={{ padding: '8px 14px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <button type="button" style={BUTTON} disabled={searching} onClick={() => runSearch(page + 1)}>{searching ? S.search.searching : S.search.more}</button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      <section style={CARD}>
        <div style={LABEL}>{S.assetId.label}</div>
        <form style={{ display: 'flex', gap: 6, marginTop: 6 }} onSubmit={(e) => { e.preventDefault(); if (extracted && confirmReplace(extracted)) onAssign(extracted, resolution); }}>
          <input
            type="text" value={assetId} placeholder={S.assetId.placeholder} aria-label={S.assetId.label}
            onChange={(e) => setAssetId(e.target.value)} style={{ ...control, flex: 1, fontFamily: MONO, fontSize: 11 }}
          />
          <button type="submit" style={BUTTON} disabled={anyBusy || !extracted || !libraryLoaded}>{S.assetId.use}</button>
        </form>
        {assetId.trim() && (
          <div className="prop-studio-asset-id" data-asset={extracted ?? ''} style={{ marginTop: 6, fontSize: 11, fontFamily: MONO, color: extracted ? OK : WARN }}>
            {extracted ? S.assetId.extracted(extracted) : S.assetId.none}
          </div>
        )}
      </section>

      <section style={CARD}>
        <div style={LABEL}>{S.upload.label}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <input type="file" accept={MODEL_FORMATS.join(',')} disabled={anyBusy || !libraryLoaded} onChange={chooseFile} style={{ fontSize: 12, color: '#fff' }} aria-label={S.upload.label} />
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }} title={S.upload.unitsTitle}>
            {S.upload.units}
            <select value={units} onChange={(e) => setUnits(e.target.value as ModelUnits)} aria-label={S.upload.units} style={{ fontSize: 12 }}>
              {MODEL_UNITS.map((u) => <option key={u} value={u}>{S.upload.unitNames[u]}</option>)}
            </select>
          </label>
        </div>
        <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9a9ca6' }}>{S.upload.formats}</p>
        {busy === 'upload' && <p style={{ margin: '8px 0 0', fontSize: 12, color: ACCENT }}>{S.upload.busy}</p>}
      </section>
    </div>
  );
};

export default CandidatePanel;
